<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="ac-header">
        New Order
      </div>
      <button
        type="button"
        class="ac-close-button"
        @click="toggleActionModal(false)"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          alt="x"
          class="ac-close"
        >
      </button>
    </div>
    <div class="ac-body-container">
      <div>
        <span class="sac__font-16 med">What would you like to order?</span>
        <div class="grid-column ai-c gap-16 jc-s margin-top-8">
          <button
            type="button"
            :class="['order-btn', orderType === 'filter' ? 'active' : '']"
            @click="toggleOrderType('filter')"
          >
            Air Filters
          </button>
          <button
            type="button"
            :class="['order-btn', orderType === 'hardware' ? 'active' : '']"
            @click="toggleOrderType('hardware')"
          >
            Everything Else
          </button>
        </div>
      </div>
      <fragment v-if="orderType">
        <div class="grid-column ai-s even gap-16 margin-top-16">
          <div class="ac-input-container">
            <div class="ac-input-header">
              Member
            </div>
            <div class="sac__font-14 n70 med">
              {{ customerInfo.name }}
            </div>
            <div class="sac__font-14 n70 med margin-top-4">
              {{ customerInfo.shippingAddress }}
            </div>
          </div>
          <div class="ac-input-container">
            <div class="ac-input-header">
              Requested By
            </div>
            <SACDropdown
              field="requestorId"
              placeholder="Select Requestor"
              :showSearch="true"
              :value="requestorId"
              :options="adminUsers"
              :change="handleRequestor"
            />
          </div>
        </div>
        <div>
          <div v-if="orderType === 'hardware'" class="ac-input-container">
            <div class="grid-column ai-c jc-s">
              Product
              <SACTooltipItem
                class="margin-top-4"
                color="#727A8F"
                :icon-size="20"
                :tip="'Vendor applies to all added products'"
              />
            </div>
            <SACDropdown
              field="selectedVendor"
              placeholder="Select Vendor"
              :value="selectedVendor"
              :options="vendors"
              :change="toggleValue"
            />
            <SACDropdown
              v-if="selectedVendor"
              :value="hardwareProductId"
              placeholder="Select Product"
              :options="productLine"
              :showSearch="true"
              field="hardwareProductId"
              :change="handleProductSelect"
            />
            <SACDropdown
              v-if="hardwareSkuOptions.length"
              field="hardwareVendorSku"
              placeholder="Select Vendor SKU"
              :value="hardwareVendorSku"
              :options="hardwareSkuOptions"
              :change="handleProductSelect"
            />
          </div>
          <div v-else-if="orderType === 'filter'" class="ac-input-container">
            <div class="ac-input-header">
              Member Air Filters
            </div>
            <SACDropdown
              placeholder="Select Air Filter"
              :options="memberFilterList"
              :value="filterCatalogId"
              field="catalogId"
              option-value="catalogId"
              :passObject="true"
              :change="handleFilterCatalog"
            />
            <SACDropdown
              v-if="filterCatalogId && filterProducts.length"
              :value="filterVendorSku"
              placeholder="Select Filter Product"
              :options="filterProducts"
              :showSearch="true"
              field="vendorSKU"
              option-value="vendorSKU"
              :passObject="true"
              :change="selectFilterCatalog"
            />
          </div>
        </div>
        <div class="order-charge-container">
          <div class="grid-column ai-c jc-s gap-16 margin-top-16">
            <SACRadio
              name="report-type"
              field="billType"
              :checked="newOrder.chargeMember"
              :change="toggleChargeMember"
              label="Billable"
            />
            <SACRadio
              name="report-type"
              field="billType"
              :checked="!newOrder.chargeMember"
              :change="toggleChargeMember"
              label="Not Billable"
            />
            <span
              v-if="errorObj.paymentErr"
              class="error-text"
            >
              {{ errorObj.paymentErr }}
            </span>
          </div>
          <div class="margin-top-16">
            <SACCheckbox
              label="Replacement"
              :checked="newOrder.replacement"
              :change="toggleReplacement"
            />
          </div>
        </div>
        <div
          v-if="newOrder.orderItems && newOrder.orderItems.length"
          class="ac-body-order-detail margin-top-16"
        >
          <div class="od-top-container">
            <div class="od-header">
              Order Detail
            </div>
            <div class="od-info">
              <div class="od-info-item">
                <OrderLineItem
                  v-for="(item, idx) in newOrder.orderItems"
                  :key="idx"
                  :item="item"
                />
              </div>
            </div>
          </div>
          <div
            v-if="newOrder.tax && newOrder.chargeMember"
            class="od-total-container"
          >
            <div>
              Tax
              ({{ newOrder.tax.jurisdiction }}):
              {{ newOrder.tax.percentage }}%
            </div>
            <div>
              ${{ taxTotal }}
            </div>
          </div>
          <div class="od-total-container">
            <div>Total</div>
            <div>
              {{ netTotal ? `$${netTotal}` : 'FREE' }}
            </div>
          </div>
        </div>
        <div
          v-if="newOrder.orderItems && newOrder.orderItems.length"
          class="grid-column ai-s gap-16 margin-top-16"
        >
          <OrderAddress
            title="Shipping Address"
            :address="newOrder.shippingAddress"
            :edit="() => editAddress('edit-shipping')"
          />
          <OrderAddress
            title="Billing Address"
            :address="newOrder.billingAddress"
            :edit="() => editAddress('edit-billing')"
          />
        </div>
      </fragment>
    </div>
    <div class="ac-footer-container">
      <div />
      <button type="button" class="ac-form-button" @click="createNewOrder">
        Create
      </button>
    </div>
    <ProcessOverlay :show="processingOrder" text="Please wait" overlayClass="ac-overlay" />
  </div>
</template>

<script>
import orderMixin from '@shared/mixins/orderMixin';
import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import SACCheckbox from '@shared/components/sac/SACCheckbox.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SACRadio from '@shared/components/sac/SACRadio.vue';
import { mapState } from 'vuex';
import SACTooltipItem from '@shared/components/sac/SACTooltip.vue';
import OrderAddress from '@shared/components/order/OrderAddress.vue';
import OrderLineItem from '@shared/components/order/OrderLineItem.vue';
import formatterMixin from '../../../mixins/formatterMixin';

export default {
  name: 'NewCustomerOrder',
  components: {
    OrderLineItem,
    OrderAddress,
    SACTooltipItem,
    SACDropdown,
    SACCheckbox,
    ProcessOverlay,
    SACRadio,
  },
  mixins: [orderMixin, formatterMixin],
  data() {
    return {
      orderType: '',
      billType: '',
      errorObj: {},
      filterCatalogId: null,
      filterVendorSku: null,
      hardwareProductId: null,
      selectedVendor: null,
      hardwareVendorSku: null,
      vendors: [{
        value: 'SmartAC',
        label: 'SmartAC',
      }, {
        value: 'OMNI',
        label: 'OMNI',
      }],
    };
  },
  computed: {
    ...mapState({
      customerSelected: (state) => state.customerDetail.customer,
      memberFilters: (state) => state.customerDetail.filters,
    }),
    customerInfo() {
      if (!this.customerSelected) {
        return null;
      }
      const {
        id,
        firstName,
        lastName,
        shippingAddress,
      } = this.customerSelected;
      let address;
      if (shippingAddress?.address1) {
        address = `${shippingAddress.address1}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`;
      }
      return {
        name: `${firstName} ${lastName}`,
        shippingAddress: address,
        id,
      };
    },
    memberFilterList() {
      if (!this.memberFilters.length) {
        return [];
      }
      return this.memberFilters.map((item) => ({
        ...item,
        label: `${item.size} · ${item.brand}`,
      }));
    },
    filterProducts() {
      if (!this.filterVendorCatalogs.length) {
        return [];
      }
      return this.filterVendorCatalogs.map((item) => ({
        ...item,
        label: this.makeFilterLabel(item),
      }));
    },
    adminUsers() {
      return this.platformUsers
        .filter((p) => p.accountStatus === 'Active')
        .map((p) => ({
          value: p.id,
          label: `${p.email}`,
        }));
    },
    productLine() {
      const hardwareProducts = this.allProducts.filter((prod) => prod.type === 'Hardware');
      hardwareProducts.sort((a, b) => {
        if (a.description < b.description) {
          return -1;
        }
        if (b.description < a.description) {
          return 1;
        }
        if (a.price < b.price) {
          return -1;
        }
        if (b.price < a.price) {
          return 1;
        }
        return 0;
      });
      return hardwareProducts.map((prod) => {
        const {
          id,
          price,
          description,
        } = prod;
        return {
          label: `${description.replace('SmartAC.com', '')} - $${price}`,
          value: id,
        };
      });
    },
    total() {
      return Number(this.newOrder.total) + this.taxTotal;
    },
    hardwareSkuOptions() {
      const allSkus = this.allHWSkus;
      if (this.hardwareProductId) {
        const filtered = this.allProducts.find((prod) => prod.id === this.hardwareProductId);
        if (filtered.sku.includes('SMBP')) {
          return allSkus.filter((x) => x.value.includes('SMBP'));
        } if (filtered.sku.includes('SMCS')) {
          return allSkus.filter((x) => x.value.includes('SMCS'));
        } if (filtered.sku.includes('SMFS')) {
          return allSkus.filter((x) => x.value.includes('SMFS'));
        } if (filtered.sku.includes('SMWS')) {
          return allSkus.filter((x) => x.value.includes('SMWS'));
        }
        return [];
      }
      return [];
    },
  },
  mounted() {
    if (this.customerSelected && this.customerSelected.id) {
      this.selectMember(this.customerSelected);
      this.$store.dispatch('vendorCatalogSearch');
    }
  },
  methods: {
    resetOrder() {
      this.filterCatalogId = null;
      this.filterVendorSku = null;
      this.hardwareProductId = null;
      this.hardwareVendorSku = null;
      this.selectedVendor = null;
      this.$store.dispatch('changeOrderValue', {
        field: 'newOrder',
        value: {},
      });
      this.selectMember(this.customerSelected);
    },
    clearOrderItem() {
      this.filterCatalogId = null;
      this.filterVendorSku = null;
      this.hardwareProductId = null;
      this.hardwareVendorSku = null;
    },
    toggleOrderType(type) {
      this.orderType = type;
      this.resetOrder();
    },
    toggleValue(f, v) {
      this[f] = v;
    },
    toggleChargeMember(_, value) {
      const newOrder = {
        ...this.newOrder,
        chargeMember: value,
      };
      this.changeOrderValue('newOrder', newOrder);
    },
    toggleReplacement() {
      const newOrder = {
        ...this.newOrder,
        replacement: !this.newOrder.replacement,
      };
      this.changeOrderValue('newOrder', newOrder);
    },
    editAddress(type) {
      const {
        shippingAddress,
        billingAddress,
      } = this.newOrder;
      let nAddress = {
        ...shippingAddress,
      };
      if (type === 'edit-billing') {
        nAddress = {
          ...billingAddress,
        };
      }
      this.changeOrderValue('nAddress', nAddress);
      this.toggleActionModal(true, type);
    },
    handleFilterCatalog(_, item) {
      if (item.catalogId) {
        this.filterCatalogId = item.catalogId;
        this.$store.dispatch('getFilterVendorCatalogs', {
          catalogId: item.catalogId,
          customerId: this.customerInfo.id,
        });
      }
    },
    makeFilterLabel(catalog) {
      let label;
      if (catalog.model) {
        label = `${catalog.model} · `;
      }
      if (catalog.brand) {
        label = `${label} ${catalog.brand} ·`;
      }
      if (catalog.productPrice && catalog.productPrice.description) {
        const filterQuality = catalog.productPrice.description.split('-').pop().trim();
        label = `${label} ${filterQuality} · ${catalog.productPrice.skuPackSize} pack · ${catalog.productPrice.skuPrice} · ${catalog.vendor}`;
      }
      return label;
    },
    selectFilterCatalog(_, vendor) {
      this.filterVendorSku = vendor.vendorSKU;
      this.changeNewOrderValue('product', {
        id: vendor.productPrice.productId,
        itemName: vendor.productPrice.description,
        price: vendor.productPrice.salePrice,
        quantity: vendor.productPrice.skuPackSize,
        type: 'Filter',
        sku: vendor.smartACSKU,
        stripePriceId: vendor.productPrice.priceId,
        thickness: vendor.thickness,
        merv: vendor.merv,
        filterHeight: vendor.height,
        filterWidth: vendor.width,
        filterCatalogId: vendor.filterCatalogId,
        vendor: vendor.vendor,
        vendorSku: vendor.vendorSKU,
        brand: vendor.brand,
      });
      this.$store.dispatch('getTaxRate');
    },
    async selectMember(item) {
      const customerItem = {
        customerId: item.id,
        ...item,
      };
      if (customerItem.customerId) {
        await this.$store.dispatch('getMemberAddressList', customerItem.customerId);
        this.changeNewOrderValue('customer', customerItem);
      }
    },
    handleRequestor(field, requestorId) {
      this.changeOrderValue(field, requestorId);
    },
    handleProductSelect(field, value) {
      if (field === 'hardwareProductId') {
        this.hardwareProductId = value;
        if (!this.hardwareSkuOptions.length) {
          this.makeHardwareOrder();
        }
      } else if (field === 'hardwareVendorSku') {
        this.hardwareVendorSku = value;
        this.makeHardwareOrder(value);
      }
    },
    makeHardwareOrder(sku) {
      const product = this.allProducts.find((prod) => prod.id === this.hardwareProductId);
      const newProduct = { ...product };
      if (sku) {
        newProduct.sku = sku;
        newProduct.vendorSku = sku;
      }
      this.changeNewOrderValue('product', newProduct);
      this.$store.dispatch('getTaxRate');
      this.clearOrderItem();
    },
    async createNewOrder() {
      this.errorObj = {};
      try {
        const res = await this.$store.dispatch('completeCustomerOrder', this.selectedVendor);
        this.$router.push(`/order/customer/${res.order.orderNumber}`);
      } catch (err) {
        const error = {};
        if (err?.response?.data) {
          const {
            message,
          } = err.response.data;
          if (message?.includes('Payment method')) {
            error.paymentErr = message;
          }
        }
        this.errorObj = error;
      }
    },
  },
};
</script>
